import { useCallback, useEffect, useMemo } from "react";
import { Button, Separator } from "@radix-ui/themes";
import { ChevronDownIcon, LightningBoltIcon } from "@radix-ui/react-icons";
import { Badge } from "@/components/ui/badge";
import DropdownMenu from "@/components/shared/dropdown-menu/DropdownMenu";
import { DatePickerWithRange } from "@/components/shared/date-range-picker/picker";
import {
  DateRange,
  formatDateRange,
  formatPresetValue,
  parseDateRange,
  PresetValue,
} from "./DateRange.model";
import styles from "@/components/shared/date-range-picker/DateRangePicker.module.scss";

function LiveBadge() {
  return (
    <Badge
      variant="secondary"
      className="bg-green-500 text-white dark:bg-green-600 pointer-events-none p-0.5 px-1 rounded-md"
    >
      <LightningBoltIcon />
      LIVE
    </Badge>
  );
}

const dateRangePresets: PresetValue[] = [
  "live",
  "today",
  "yesterday",
  "15m",
  "1h",
  "2h",
  "4h",
  "1d",
  "2d",
  "3d",
  "1w",
  "15d",
  "1mo",
  "2mo",
  "3mo",
  "6mo",
];

interface DateRangePickerProps {
  value: DateRange;
  onChange: (newDateRange: DateRange) => void;
  hideRange?: boolean;
  size?: "1" | "2" | "3";
  liveInterval?: number;
}

export function DateRangePicker({
  value,
  onChange,
  hideRange,
  size = "1",
  liveInterval = 15_000,
}: DateRangePickerProps) {
  const { dateFrom, dateTo } = useMemo(() => parseDateRange(value), [value]);

  const items = useMemo(() => {
    return dateRangePresets.map((item) => {
      if (item === "live") {
        return {
          icon: <LiveBadge />,
          label: formatPresetValue(item),
          onClick: () => onChange({ type: "preset", value: item }),
        };
      }

      return {
        label: formatPresetValue(item),
        onClick: () => onChange({ type: "preset", value: item }),
      };
    });
  }, [onChange]);

  const handlePickerChange = useCallback(
    (newDateRange: { dateFrom: string; dateTo: string }) => {
      onChange({
        type: "custom",
        dateFrom: newDateRange.dateFrom,
        dateTo: newDateRange.dateTo,
      });
    },
    [onChange]
  );

  useEffect(() => {
    if (value.type === "preset" && value.value === "live") {
      const timeout = setTimeout(() => {
        onChange({
          type: "preset",
          value: "live",
        });
      }, liveInterval);

      return () => clearTimeout(timeout);
    }
  }, [value, liveInterval, onChange]);

  return (
    <>
      <div className={styles.Trigger}>
        <DropdownMenu items={items}>
          <DropdownMenu.Trigger>
            <Button
              color={"gray"}
              variant="soft"
              size={size}
              highContrast
              className="border-0 rounded-[3px] w-[180px] justify-between"
            >
              <span className="flex items-center gap-1">
                {value.type === "preset" && value.value === "live" && (
                  <LiveBadge />
                )}
                {formatDateRange(value)}
              </span>

              <ChevronDownIcon />
            </Button>
          </DropdownMenu.Trigger>
        </DropdownMenu>

        {!hideRange && (
          <>
            <Separator orientation="vertical" />
            <DatePickerWithRange
              className="border-0 rounded-[3px]"
              size={size}
              dateFrom={dateFrom}
              dateTo={dateTo}
              setDateRange={handlePickerChange}
            />
          </>
        )}
      </div>
    </>
  );
}
